/**
 * Feedback submission — posts to a Google Apps Script webhook bound to a
 * Google Sheet. The script writes a row per submission and uploads
 * attachments to Drive, pasting shareable links into the sheet row.
 *
 * The webhook URL is configured via `VITE_FEEDBACK_WEBHOOK_URL`. When unset,
 * the feedback button doesn't render (see FeedbackButton).
 *
 * IMPORTANT: we POST with `text/plain` content-type so the request stays
 * "simple" by CORS rules — Apps Script web apps don't reliably handle
 * preflight (OPTIONS) requests, but they accept text/plain bodies fine
 * (the script parses JSON from the raw body anyway).
 */

export interface FeedbackAttachment {
  name: string;
  type: string;
  size: number;
  data: string; // base64
}

export interface FeedbackPayload {
  category: string;
  message: string;
  /** Optional contact name — sender's call. */
  name?: string;
  /** Optional contact email — only present if the sender wants a reply. */
  email?: string;
  /** Anything they'd like to add about themselves / context (optional). */
  role?: string;
  url: string;
  user: string;
  browser: string;
  appVersion: string;
  timestamp: number;
  attachments: FeedbackAttachment[];
}

export function isFeedbackConfigured(): boolean {
  return Boolean(import.meta.env.VITE_FEEDBACK_WEBHOOK_URL);
}

export async function submitFeedback(opts: {
  category: string;
  message: string;
  files: File[];
  name?: string;
  email?: string;
  role?: string;
}): Promise<void> {
  const url = import.meta.env.VITE_FEEDBACK_WEBHOOK_URL;
  if (!url) throw new Error("Feedback isn't configured (VITE_FEEDBACK_WEBHOOK_URL is unset).");

  const attachments: FeedbackAttachment[] = await Promise.all(
    opts.files.map(async (file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      data: await fileToBase64(file),
    })),
  );

  const payload: FeedbackPayload = {
    category: opts.category,
    message: opts.message,
    name: opts.name?.trim() || undefined,
    email: opts.email?.trim() || undefined,
    role: opts.role?.trim() || undefined,
    url: window.location.href,
    user: localStorage.getItem("lexikon.displayName") || "anonymous",
    browser: navigator.userAgent,
    appVersion: "lexikon-1",
    timestamp: Date.now(),
    attachments,
  };

  const res = await fetch(url, {
    method: "POST",
    // text/plain avoids CORS preflight. Apps Script reads the raw body.
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify(payload),
    redirect: "follow",
  });
  if (!res.ok) throw new Error(`Feedback server returned ${res.status}`);
  // Apps Script returns JSON; ignore parse failures.
  try {
    const data = await res.json();
    if (data?.success === false) throw new Error(data.error || "Unknown server error");
  } catch {
    // Tolerate non-JSON OK responses
  }
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}
